import {FormValuesProps} from "../modals/notificationModals/NewNotificationModal";
import Step1Fields from "../messageCreationFlow/step1/Step1Fields";
import Step2Fields from "../messageCreationFlow/step2/Step2Fields";
import Step3Fields from "../messageCreationFlow/step3/Step3Fields";
import "./message-edit-flow.css";
import {Button} from "antd";
import {CloseOutlined, DeleteFilled, SendOutlined, ThunderboltFilled} from "@ant-design/icons";
import MessagePreview from "../messagePreview/MessagePreview";
import useUserContext from "../../hooks/useUserContext";
import {useState} from "react";
import useAppContext from "../../hooks/useAppContext";
import useToast from "../../hooks/useToast";
import {ToastSkin} from "../../context/ToastContext";
import {useNavigate} from "react-router-dom";
import {LANGUAGES} from "../../utils/languages";
import TestTemplateButton from "./testTemplateButton/TestTemplateButton";

interface MessageEditFlowProps {
    flowId: number;
    flowTriggerId: number;
    messageId: number;
    formValues: FormValuesProps;
    setFormValues: (newValues: FormValuesProps) => void;
    onEdit: () => Promise<boolean>;
    onDelete: () => Promise<boolean>;
    isLoading: boolean;
    onClose: () => void;
}

const MessageEditFlow = ({ flowId, flowTriggerId, messageId, formValues, setFormValues, onClose, onEdit, isLoading, onDelete }: MessageEditFlowProps) => {
    const { title, language, content, isCoupon, isWithImage, triggerDelay, isActive, distribution } = formValues;
    const { showToast, closeToast } = useToast();
    const setTitle = (newValue: string) => setFormValues({ ...formValues, title: newValue });
    const setLanguage = (newValue: string) => setFormValues({ ...formValues, language: newValue });
    const setContent = (newValue: string) => setFormValues({ ...formValues, content: newValue });
    const setIsWithImage = (newValue: boolean) => setFormValues({ ...formValues, isWithImage: newValue });
    const setIsCoupon = (newValue: boolean) => setFormValues({ ...formValues, isCoupon: newValue });
    const setTriggerDelay = (newValue: number | null) => setFormValues({ ...formValues, triggerDelay: newValue });
    const setIsActive = (newValue: boolean) => setFormValues({ ...formValues, isActive: newValue });
    const setDistribution = (newValue: number) => setFormValues({ ...formValues, distribution: newValue });
    const setButton1Text = (newValue: string) => setFormValues({ ...formValues, button1Text: newValue });
    const setButton2Text = (newValue: string) => setFormValues({ ...formValues, button2Text: newValue });

    const selectedLanguage = LANGUAGES.find(lang => lang.key === language);
    const direction = selectedLanguage?.direction === 'rtl' ? 'rtl' : 'ltr';

    const onAttemptingDelete = () => {
        showToast({
            skin: ToastSkin.danger,
            text: 'Are you sure you want to completely delete this template? This is irreversible',
            displayTime: 10000,
            buttons: [
                { text: 'Yes', onClick: onDelete },
                { text: 'No, Cancel', onClick: closeToast }
            ] })
    }

    return (
        <div className="edit-flow-container">
            <div className="left">
                <Step1Fields title={title} setTitle={setTitle} language={language} setLanguage={setLanguage} content={content} setContent={setContent} isWithImage={isWithImage} setIsWithImage={setIsWithImage} isCoupon={isCoupon} setIsCoupon={setIsCoupon} id={messageId} flowTriggerId={flowTriggerId} direction={direction} />
                {flowTriggerId !== 11 ? <Step2Fields triggerDelay={triggerDelay} setTriggerDelay={setTriggerDelay} flowTriggerId={flowTriggerId} /> : null}
                <Step3Fields isActive={isActive} setIsActive={setIsActive} distribution={distribution} setDistribution={setDistribution} />
            </div>
            <div className="right">
                <MessagePreview
                    text={content}
                    isWithImage={isWithImage}
                    direction={direction}
                    flowTriggerId={flowTriggerId}
                    language={language}
                    button1Text={formValues.button1Text}
                    button2Text={formValues.button2Text}
                    setButton1Text={setButton1Text}
                    setButton2Text={setButton2Text}
                />
                <div className="buttons">
                    <div className="button-row">
                        <Button type="primary" size="large" className="next-step-button" loading={isLoading} onClick={onEdit} icon={<ThunderboltFilled />} disabled={!title || !content || triggerDelay === undefined}>Update Template</Button>
                        <TestTemplateButton title={title} content={content} language={language} flowId={flowId} messageId={messageId} />
                    </div>
                    <div className="button-row">
                        <Button type="default" size="large" className="back-step-button" onClick={onClose} icon={<CloseOutlined />}>Close</Button>
                        <Button type="default" size="large" className="danger-button back-step-button" onClick={onAttemptingDelete} icon={<DeleteFilled />}>Delete Template</Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MessageEditFlow;